import React, { useState, useEffect } from 'react';
import { saasData, supabase } from '../../../lib/supabase';
import { 
  getTeacherSubjects, 
  getTeacherECAs, 
  getTeacherCommittees, 
  getTeacherDuties,
  getTeacherClassAssignments 
} from '../TeacherDirectoryHelpers';

export const useTeacherData = () => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classMembers, setClassMembers] = useState([]);
  const [teacherAssignments, setTeacherAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        let teachersData = [];
        let classesData = [];
        let membersData = [];
        let assignmentsData = [];

        // Try to fetch from Supabase first
        if (supabase && saasData) {
          try {
            // Get current tenant ID (default to first tenant for now)
            const tenantId = 'tn_01'; // This should come from user context or tenant selection
            // Fetch teachers using saasData function
            const { data: teachersResult, error: teachersError } = await saasData.getTenantTeachers(tenantId);
            if (teachersError) throw teachersError;

            // Fetch classes using saasData function
            const { data: classesResult, error: classesError } = await saasData.getTenantClasses(tenantId);
            if (classesError) throw classesError;

            // Fetch class members using saasData function
            const { data: membersResult, error: membersError } = await saasData.getTenantClassMembers(tenantId);
            if (membersError) throw membersError;

            // Fetch teacher assignments (class master, vice class master, etc.)
            const { data: assignmentsResult, error: assignmentsError } = await saasData.getTenantTeacherAssignments(tenantId);
            if (assignmentsError) throw assignmentsError;

            teachersData = teachersResult || [];
            classesData = classesResult || [];
            membersData = membersResult || [];
            assignmentsData = assignmentsResult || [];
            
            // Debug: Log what we got
            console.log('Supabase data fetched:', {
              teachers: teachersData.length,
              classes: classesData.length,
              members: membersData.length,
              assignments: assignmentsData.length
            });
          } catch (supabaseError) {
            console.warn('Supabase fetch failed, falling back to mock data:', supabaseError);
            // Fall back to mock data if Supabase fails
          }
        }

        // If no data from Supabase, use mock data
        if (teachersData.length === 0) {
          console.log('No Supabase data, using mock data');
          // Use mock data from saasData or fallback
          const mockTeachers = [
            {
              teacher_id: '1',
              name: '張老師',
              english_name: 'Mr. Zhang',
              department: '數學科',
              title: '科主任',
              email: 'zhang@school.edu.hk',
              phone: '2345-6789',
              office: 'A座3樓',
              status: 'active',
              avatar: null,
              bio: '資深數學教師，專注於中學數學教育',
              qualifications: ['香港大學數學系學士', '香港中文大學教育文憑'],
              // Extended fields
              subject_specialty: '數學',
              position: '科主任',
              taught_subj1: '數學',
              taught_subj2: '高等數學',
              eca1: '數學學會',
              member1: '學術委員會',
              duty1: '早會司儀'
            },
            {
              teacher_id: '2',
              name: '李老師',
              english_name: 'Ms. Li',
              department: '中文科',
              title: '教師',
              email: 'li@school.edu.hk',
              phone: '2345-6790',
              office: 'B座2樓',
              status: 'active',
              avatar: null,
              bio: '中文科教師，熱愛文學創作',
              qualifications: ['香港中文大學中文系學士', '香港大學教育文憑'],
              // Extended fields
              subject_specialty: '中文',
              position: '教師',
              taught_subj1: '中文',
              taught_subj2: '中國文學',
              eca1: '文學社',
              member1: '德育委員會',
              duty1: '圖書館管理'
            },
            {
              teacher_id: '3',
              name: '王老師',
              english_name: 'Mr. Wang',
              department: '英文科',
              title: '副科主任',
              email: 'wang@school.edu.hk',
              phone: '2345-6791',
              office: 'C座1樓',
              status: 'active',
              avatar: null,
              bio: '英文科教師，專注於英語會話教學',
              qualifications: ['香港大學英文系學士', '香港中文大學教育文憑'],
              // Extended fields
              subject_specialty: '英文',
              position: '副科主任',
              taught_subj1: '英文',
              taught_subj2: '英語會話',
              eca1: '英語學會',
              member1: '語言委員會',
              duty1: '早會司儀'
            }
          ];

          const mockClasses = [
            {
              class_id: '1',
              name: 'S6A',
              grade: '中六',
              class_master: '1',
              vice_class_master: '2',
              student_count: 32,
              schedule: '週一至週五 8:00-9:00',
              room: 'A301',
              status: 'active',
              description: '中六數學班'
            },
            {
              class_id: '2',
              name: 'S5B',
              grade: '中五',
              class_master: '2',
              vice_class_master: '3',
              student_count: 30,
              schedule: '週一至週五 9:00-10:00',
              room: 'B201',
              status: 'active',
              description: '中五中文班'
            }
          ];

          const mockClassMembers = [
            {
              id: '1',
              class_id: '1',
              student_id: '1',
              student_name: '陳同學'
            },
            {
              id: '2',
              class_id: '1',
              student_id: '2',
              student_name: '林同學'
            }
          ];

          setTeachers(mockTeachers);
          setClasses(mockClasses);
          setClassMembers(mockClassMembers);
          
          // Set empty teacher assignments for mock data
          setTeacherAssignments([]);
        } else {
          // Use data from Supabase
          console.log('Using Supabase data');
          setTeachers(teachersData);
          setClasses(classesData);
          setClassMembers(membersData);
          
          // Process teacher assignments to link teachers with classes
          if (assignmentsData && assignmentsData.length > 0) {
            console.log('Processing teacher assignments...');
            setTeacherAssignments(assignmentsData);
          }
        }

      } catch (err) {
        console.error('Error fetching teacher data:', err);
        setError(err.message || 'Failed to fetch teacher data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    teachers,
    classes,
    classMembers,
    teacherAssignments,
    loading,
    error
  };
};
